import Api from "../../../../../../../../../core/services/api/api";

/**
 * @typedef {{ id: *, title: string }} IndustryPropertyOption
 */

/**
 * In-memory industry-property-id → options cache for one import run.
 */
export class IndustryPropertyOptionsLookupStore {
    constructor() {
        /** @type {Map<string|number, IndustryPropertyOption[]|null>} */
        this._entryByPropertyId = new Map();
    }

    clear() {
        this._entryByPropertyId.clear();
    }

    /**
     * Merges an option created during preventative correction into the import-run cache.
     *
     * @param {string|number} propertyId
     * @param {IndustryPropertyOption} option
     */
    registerCreatedOption(propertyId, option) {
        if (propertyId == null || !option?.id) {
            return;
        }
        const key = String(propertyId);
        const existing = this._entryByPropertyId.get(key) ?? [];
        const hasDuplicate = existing.some(
            (entry) =>
                String(entry.id) === String(option.id) ||
                (entry.title ?? '').trim().toUpperCase() === (option.title ?? '').trim().toUpperCase(),
        );
        if (hasDuplicate) {
            return;
        }
        this._entryByPropertyId.set(key, [...existing, option]);
    }

    /**
     * @param {Array<string|number>} propertyIds
     * @returns {Promise<Record<string, IndustryPropertyOption[]>>}
     */
    async #fetchFromServerOnce(propertyIds) {
        if (!propertyIds?.length) {
            return {};
        }
        const results = await Promise.all(
            propertyIds.map(async (propertyId) => {
                const response = await Api.listIndustryPropertyPropertyValues(propertyId);
                if (!response?.resultFlag) {
                    return {propertyId, options: []};
                }
                const options = (response.data ?? []).map((e) => ({
                    id: e.id,
                    title: e.content ?? '',
                }));
                return {propertyId, options};
            }),
        );
        return Object.fromEntries(results.map((r) => [String(r.propertyId), r.options]));
    }

    /**
     * @param {Array<string|number>} propertyIds
     * @param {boolean} [disableCache=false]
     * @returns {Promise<Record<string, IndustryPropertyOption[]>>}
     */
    async resolve(propertyIds, disableCache = false) {
        const unique = [...new Set((propertyIds ?? []).filter((id) => id != null && id !== ''))];
        if (!unique.length) {
            return {};
        }

        const missing = unique.filter((id) => disableCache || !this._entryByPropertyId.has(String(id)));

        if (missing.length > 0) {
            const fresh = await this.#fetchFromServerOnce(missing);
            for (const id of missing) {
                const key = String(id);
                this._entryByPropertyId.set(key, fresh[key] ?? []);
            }
        }

        const out = {};
        for (const id of unique) {
            const key = String(id);
            const entry = this._entryByPropertyId.get(key);
            if (entry) {
                out[key] = entry;
            }
        }
        return out;
    }
}
